import axios from 'axios'
import { API_URL } from '@/shared/constants/env'
import { Toast } from '@capacitor/toast'
import { Capacitor } from '@capacitor/core'

export const client = axios.create({
  baseURL: API_URL,
  timeout: 60000,
})

export const setBearerToken = (token: string) => {
  if (token) {
    client.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete client.defaults.headers.common['Authorization']
  }
}

/**
 * Extract data from API response
 * API may return { data: {...} } or direct data
 */
export function extractData<T>(response: { data: T | { data: T } }): T {
  if (response.data && typeof response.data === 'object' && 'data' in response.data) {
    return (response.data as { data: T }).data
  }
  return response.data as T
}

/**
 * Create DOM toast element
 */
const createDomToast = (message: string) => {
  // Remove existing toast
  const existing = document.getElementById('api-toast')
  if (existing) existing.remove()

  const toast = document.createElement('div')
  toast.id = 'api-toast'
  toast.textContent = message
  toast.style.cssText = `
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: #1e293b;
    color: white;
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 14px;
    font-family: system-ui, -apple-system, sans-serif;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    animation: toast-in 0.3s ease;
  `

  // Add animation keyframes if not exists
  if (!document.getElementById('toast-styles')) {
    const style = document.createElement('style')
    style.id = 'toast-styles'
    style.textContent = `
      @keyframes toast-in {
        from { opacity: 0; transform: translateX(-50%) translateY(10px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `
    document.head.appendChild(style)
  }

  document.body.appendChild(toast)

  // Auto remove after 4 seconds
  setTimeout(() => {
    toast.style.opacity = '0'
    toast.style.transform = 'translateX(-50%) translateY(10px)'
    toast.style.transition = 'all 0.3s ease'
    setTimeout(() => toast.remove(), 300)
  }, 4000)
}

/**
 * Show error toast message
 */
export const showError = async (message: string) => {
  // Use native Toast on capacitor platforms, DOM fallback on web
  if (Capacitor.isNativePlatform()) {
    await Toast.show({
      text: message,
      duration: 'long',
      position: 'bottom',
    })
  } else {
    createDomToast(message)
  }
}

// Request interceptor
client.interceptors.request.use(
  (config) => {
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Extract error message
    const message = error.response?.data?.message 
      || error.response?.data?.error 
      || error.message 
      || 'Unknown error'

    // Show toast for API errors
    await showError(message)

    if (error.response?.status === 401) {
      // Clear auth on 401 - will redirect to login via router guard
      localStorage.clear()
      sessionStorage.clear()
    }
    
    return Promise.reject(error)
  }
)